import React, { useState } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';

const AdminLayout: React.FC = () => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-red-50 to-white">
        <div className="relative">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-red-200 border-t-red-500"></div>
          <div className="absolute inset-0 animate-ping rounded-full h-16 w-16 border-4 border-red-300 opacity-20"></div>
        </div>
      </div>
    );
  }

  if (!user || user.role !== 'admin') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-red-50 via-white to-red-50">
        <div className="text-center bg-white rounded-2xl shadow-2xl p-8 max-w-md mx-4 border-2 border-red-100">
          <div className="mb-6">
            <div className="inline-block p-4 bg-red-50 rounded-full mb-4">
              <div className="w-16 h-16 bg-gradient-to-br from-red-400 to-red-600 rounded-full flex items-center justify-center text-white text-2xl font-bold shadow-lg">
                !
              </div>
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Không có quyền truy cập</h1>
            <p className="text-gray-600">Bạn cần quyền admin để truy cập trang này.</p>
          </div>
          <button
            onClick={() => navigate('/')}
            className="px-8 py-3 bg-gradient-to-r from-red-500 to-red-600 text-white rounded-xl hover:from-red-600 hover:to-red-700 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 font-semibold"
          >
            Về trang chủ
          </button>
        </div>
      </div>
    );
  }

  const menuItems = [
    { to: '/admin/users', label: 'Người dùng' },
    { to: '/admin/fake-users', label: 'Quản lý User Ảo' },
    { to: '/admin/videos', label: 'Tải video' },
    { to: '/admin/enrollments', label: 'Ghi danh' },
    { to: '/admin/categories', label: 'Danh mục khóa học' },
    { to: '/admin/post-categories', label: 'Danh mục bài viết' },
    { to: '/admin/school-slides', label: 'Slide trường phái' },
    { to: '/admin/courses', label: 'Quản lý khóa học' },
    { to: '/admin/posts', label: 'Quản lý bài đăng' },
    { to: '/admin/tasks', label: 'Nhiệm vụ hệ thống' },
    { to: '/admin/course-tasks', label: 'Nhiệm vụ khóa học' },
    { to: '/admin/levels', label: 'Quản lý cấp độ' },
    { to: '/admin/youtube-notifications', label: 'Thông báo YouTube' },
    { to: '/admin/youtube-view', label: 'Check View YouTube' },
    { to: '/admin/free-keys', label: 'Chia sẻ Key Free' },
    { to: '/admin/transcript-tokens', label: 'Transcript Tokens' },
    { to: '/admin/events', label: 'Sự kiện Năm Mới' },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-red-50 via-white to-red-50">
      {/* Mobile Header */}
      <div className="lg:hidden bg-white border-b-2 border-red-100 sticky top-0 z-40 shadow-md">
        <div className="flex items-center justify-between px-4 py-4">
          <div>
            <h1 className="text-2xl font-extrabold bg-gradient-to-r from-red-500 to-red-600 bg-clip-text text-transparent">
              Admin Panel
            </h1>
            <p className="text-sm text-gray-700 font-semibold mt-0.5">Quản trị hệ thống</p>
          </div>
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="p-2.5 rounded-xl bg-red-50 hover:bg-red-100 transition-all duration-300 hover:scale-110 active:scale-95"
            aria-label="Toggle menu"
          >
            <div className="w-6 h-6 flex flex-col justify-center gap-1.5">
              <span className={`block h-0.5 bg-red-600 rounded-full transition-all duration-300 ${sidebarOpen ? 'rotate-45 translate-y-2' : ''}`}></span>
              <span className={`block h-0.5 bg-red-600 rounded-full transition-all duration-300 ${sidebarOpen ? 'opacity-0' : ''}`}></span>
              <span className={`block h-0.5 bg-red-600 rounded-full transition-all duration-300 ${sidebarOpen ? '-rotate-45 -translate-y-2' : ''}`}></span>
            </div>
          </button>
        </div>
      </div>

      <div className="flex">
        {/* Sidebar */}
        <aside
          className={`
            fixed lg:sticky top-0 left-0 h-screen lg:h-auto
            w-72 bg-white border-r-2 border-red-100 z-30
            transform transition-transform duration-500 ease-out
            ${sidebarOpen ? 'translate-x-0 shadow-2xl' : '-translate-x-full lg:translate-x-0'}
            overflow-y-auto
            scrollbar-thin scrollbar-thumb-red-200 scrollbar-track-red-50
          `}
        >
          <div className="p-6">
            {/* Desktop Header */}
            <div className="hidden lg:block mb-8 pb-6 border-b-2 border-red-200">
              <h1 className="text-3xl font-extrabold bg-gradient-to-r from-red-500 to-red-600 bg-clip-text text-transparent mb-2">
                Admin Panel
              </h1>
              <p className="text-base text-gray-700 font-medium">Quản trị hệ thống</p>
            </div>

            <nav className="space-y-2">
              {menuItems.map((item, index) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  onClick={() => setSidebarOpen(false)}
                  className={({ isActive }) =>
                    `block px-5 py-4 rounded-xl transition-all duration-300 transform hover:scale-[1.02] active:scale-100 ${isActive
                      ? 'bg-gradient-to-r from-red-500 to-red-600 text-white shadow-lg shadow-red-500/40 font-bold text-base'
                      : 'text-gray-800 hover:bg-red-50 hover:text-red-700 hover:shadow-md hover:border-l-4 hover:border-red-500 font-semibold text-base'
                    }`
                  }
                  style={{ animationDelay: `${index * 50}ms` }}
                >
                  <span>{item.label}</span>
                </NavLink>
              ))}
            </nav>
          </div>
        </aside>

        {/* Overlay for mobile */}
        {sidebarOpen && (
          <div
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-20 lg:hidden animate-in fade-in duration-300"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        {/* Main Content */}
        <main className="flex-1 min-w-0">
          <div className="p-4 sm:p-5 md:p-6 lg:p-8 xl:p-10">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;


